/**
 * PageSettingsDialog
 * Dialog for editing page settings including SEO, navigation, and metadata.
 */

import { useState, useEffect } from 'react'
import { Loader2, Settings, AlertTriangle, FileCog } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ImageUploadField } from '@/components/ui/ImageUploadField'
import { useUpdatePage, useHomePage } from '@/hooks/use-pages'
import { validatePageSlug } from '@limo/shared'
import type { PageWithSectionCount } from '@/hooks/use-pages'

interface PageSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  page: PageWithSectionCount
}

export function PageSettingsDialog({
  open,
  onOpenChange,
  page,
}: PageSettingsDialogProps) {
  // General settings
  const [title, setTitle] = useState(page.title)
  const [slug, setSlug] = useState(page.slug)
  const [slugError, setSlugError] = useState<string | null>(null)

  // SEO settings
  const [metaTitle, setMetaTitle] = useState(page.meta_title || '')
  const [metaDescription, setMetaDescription] = useState(page.meta_description || '')
  const [ogImageUrl, setOgImageUrl] = useState(page.og_image_url || '')

  // Navigation settings
  const [showInNavigation, setShowInNavigation] = useState(page.show_in_navigation)
  const [showInFooter, setShowInFooter] = useState(page.show_in_footer ?? false)
  const [showHeader, setShowHeader] = useState(page.show_header ?? true)
  const [navigationLabel, setNavigationLabel] = useState(page.navigation_label || '')
  const [navigationOrder, setNavigationOrder] = useState(page.navigation_order ?? 0)

  const [activeTab, setActiveTab] = useState('general')

  const updatePage = useUpdatePage()

  // Which page actually serves as the site's homepage. A page can carry
  // page_type 'home' without being it — the viewer resolves the homepage from
  // the home_page_id setting first, and only falls back to page_type.
  const { data: homePageId } = useHomePage(page.account_id)

  // Converting to a custom page is a one-way, immediate action rather than part
  // of Save: it changes what the rest of this dialog allows (the slug field
  // unlocks), so it has to land before the operator edits the slug.
  const [isConverting, setIsConverting] = useState(false)
  const [pageType, setPageType] = useState(page.page_type)

  // Sync state when page prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setTitle(page.title)
      setSlug(page.slug)
      setSlugError(null)
      setMetaTitle(page.meta_title || '')
      setMetaDescription(page.meta_description || '')
      setOgImageUrl(page.og_image_url || '')
      setShowInNavigation(page.show_in_navigation)
      setShowInFooter(page.show_in_footer ?? false)
      setShowHeader(page.show_header ?? true)
      setNavigationLabel(page.navigation_label || '')
      setNavigationOrder(page.navigation_order ?? 0)
      setActiveTab('general')
    }
    setPageType(page.page_type)
  }, [open, page])

  // Validate slug on change
  const handleSlugChange = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setSlug(normalized)
    setSlugError(validatePageSlug(normalized, { allowEmpty: pageType === 'home' }))
  }

  const handleSave = async () => {
    // Validate slug
    const error = validatePageSlug(slug, { allowEmpty: pageType === 'home' })
    if (error) {
      setSlugError(error)
      setActiveTab('general')
      return
    }

    try {
      await updatePage.mutateAsync({
        pageId: page.id,
        updates: {
          title,
          slug,
          meta_title: metaTitle || null,
          meta_description: metaDescription || null,
          og_image_url: ogImageUrl || null,
          show_in_navigation: showInNavigation,
          show_in_footer: showInFooter,
          show_header: showHeader,
          navigation_label: navigationLabel || null,
          navigation_order: navigationOrder,
        },
      })
      onOpenChange(false)
    } catch (err) {
      // Error is handled by the hook's onError
      console.error('Failed to update page settings:', err)
    }
  }

  // Check if anything has changed
  const hasChanges =
    title !== page.title ||
    slug !== page.slug ||
    metaTitle !== (page.meta_title || '') ||
    metaDescription !== (page.meta_description || '') ||
    ogImageUrl !== (page.og_image_url || '') ||
    showInNavigation !== page.show_in_navigation ||
    showInFooter !== (page.show_in_footer ?? false) ||
    showHeader !== (page.show_header ?? true) ||
    navigationLabel !== (page.navigation_label || '') ||
    navigationOrder !== (page.navigation_order ?? 0)

  // Show warning if slug changed on published page
  const slugChanged = slug !== page.slug
  const showSlugWarning = slugChanged && page.is_published

  // Derived from local state, not the prop, so the slug unlocks the moment the
  // conversion succeeds rather than waiting for the pages query to refetch.
  const isSystemPage = pageType !== 'custom'

  // The live homepage must keep page_type 'home': the viewer's fallback
  // resolves the homepage by that type when home_page_id is unset, and its
  // slug is '' — which nothing else may take, since slugs are unique per
  // account. Duplicates that merely carry the type are free to convert.
  const isLiveHomePage = homePageId ? page.id === homePageId : page.slug === ''
  const canConvertToCustom = isSystemPage && !isLiveHomePage

  const handleConvertToCustom = async () => {
    setIsConverting(true)
    try {
      await updatePage.mutateAsync({
        pageId: page.id,
        updates: { page_type: 'custom' },
      })
      setPageType('custom')
    } catch (err) {
      // Surfaced by the hook's onError toast.
      console.error('Failed to convert page to custom:', err)
    } finally {
      setIsConverting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Page Settings
          </DialogTitle>
          <DialogDescription>
            Configure settings for "{page.title}"
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <TabsContent value="general" className="mt-0 space-y-4 pr-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="page-title">Page Title</Label>
                <Input
                  id="page-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., About Us"
                />
                <p className="text-xs text-muted-foreground">
                  The title displayed in the browser tab and navigation
                </p>
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="page-slug">URL Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/</span>
                  <Input
                    id="page-slug"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder={pageType === 'home' ? '(root)' : 'page-slug'}
                    className="flex-1"
                    disabled={isSystemPage}
                  />
                </div>
                {slugError && (
                  <p className="text-sm text-destructive">{slugError}</p>
                )}

                {/* A system page used to dead-end here: the slug was locked with
                    no way to change the page's type, so a page created as the
                    wrong type could never be corrected. */}
                {canConvertToCustom && (
                  <div className="rounded-md border bg-muted/40 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          This is a {pageType} page
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Converting it to a custom page unlocks the URL slug. The page
                          keeps its sections and content, and the site renders it
                          exactly as it does now.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={handleConvertToCustom}
                        disabled={isConverting}
                      >
                        {isConverting ? (
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FileCog className="mr-2 h-3.5 w-3.5" />
                        )}
                        Convert to custom
                      </Button>
                    </div>
                  </div>
                )}

                {isSystemPage && !canConvertToCustom && (
                  <p className="text-xs text-muted-foreground">
                    This page is the site&apos;s homepage, so its type and URL are fixed.
                    To use a different homepage, set one from the pages list first.
                  </p>
                )}
              </div>

              {/* Slug change warning */}
              {showSlugWarning && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Changing the URL of a published page may break existing links and bookmarks.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="seo" className="mt-0 space-y-4 pr-4">
              {/* Meta Title */}
              <div className="space-y-2">
                <Label htmlFor="meta-title">Meta Title</Label>
                <Input
                  id="meta-title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder={title || 'Page title for search engines'}
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground">
                  {metaTitle.length}/60 characters. Leave empty to use page title.
                </p>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <Label htmlFor="meta-description">Meta Description</Label>
                <Textarea
                  id="meta-description"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Brief description for search engine results..."
                  rows={3}
                  maxLength={160}
                />
                <p className="text-xs text-muted-foreground">
                  {metaDescription.length}/160 characters. Appears in search results.
                </p>
              </div>

              {/* OG Image */}
              <div className="space-y-2">
                <ImageUploadField
                  label="Social Share Image (OG Image)"
                  value={ogImageUrl}
                  onChange={setOgImageUrl}
                  accountId={page.account_id}
                  placeholder="https://example.com/image.jpg"
                  description="Image shown when sharing this page on social media. Recommended: 1200x630px"
                />
              </div>
            </TabsContent>

            <TabsContent value="navigation" className="mt-0 space-y-6 pr-4">
              {/* Show in Header Navigation */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="show-in-nav" className="text-base">
                    Show in Header Navigation
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Display this page in the main navigation menu
                  </p>
                </div>
                <Switch
                  id="show-in-nav"
                  checked={showInNavigation}
                  onCheckedChange={setShowInNavigation}
                />
              </div>

              {/* Site header on this page */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="show-header" className="text-base">
                    Show Site Header
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Turn off when the first section supplies its own navigation —
                    the Hero with Booking Widget does — so visitors don&apos;t see
                    two headers. The footer is unaffected.
                  </p>
                </div>
                <Switch
                  id="show-header"
                  checked={showHeader}
                  onCheckedChange={setShowHeader}
                />
              </div>

              {/* Show in Footer Navigation */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="show-in-footer" className="text-base">
                    Show in Footer Navigation
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Display this page in the footer links section
                  </p>
                </div>
                <Switch
                  id="show-in-footer"
                  checked={showInFooter}
                  onCheckedChange={setShowInFooter}
                />
              </div>

              {/* Navigation Label */}
              <div className="space-y-2">
                <Label htmlFor="nav-label">Navigation Label</Label>
                <Input
                  id="nav-label"
                  value={navigationLabel}
                  onChange={(e) => setNavigationLabel(e.target.value)}
                  placeholder={title || 'Custom navigation label'}
                />
                <p className="text-xs text-muted-foreground">
                  Custom text for navigation menus. Leave empty to use page title.
                </p>
              </div>

              {/* Navigation Order */}
              <div className="space-y-2">
                <Label htmlFor="nav-order">Navigation Order</Label>
                <Input
                  id="nav-order"
                  type="number"
                  value={navigationOrder}
                  onChange={(e) => setNavigationOrder(parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
                <p className="text-xs text-muted-foreground">
                  Lower numbers appear first in navigation (0-100)
                </p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updatePage.isPending || !hasChanges || !title.trim()}
          >
            {updatePage.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
